<script setup lang="ts">
import NavLink from '~/components/site-header/NavLink.vue';
import type { NavigationLinkFragment } from '#gql';

defineProps<{
  navLinks: NavigationLinkFragment[];
  isOpen: boolean;
}>();

const { path } = useRoute();
const isActiveRoute = (slug: string) =>
  path === `/${slug}` || path.startsWith(`/${slug}/`);

const counterString = (index: number) => {
  return (index + 1).toString().padStart(2, '0');
};
</script>

<template>
  <nav class="main-navigation" v-show="isOpen">
    <TransitionGroup name="nav-item-transition" tag="ul" class="list" appear>
      <li
        v-for="(item, index) in navLinks"
        :key="item.id"
        class="item"
        :style="{ '--index': index }"
      >
        <span class="counter text-3xs _hidden-vs">{{
          counterString(index)
        }}</span>
        <NavLink
          class="link text-2xl-vl text-xl-vs"
          :class="{ '-is-active': isActiveRoute(item.link?.slug ?? '') }"
          :data="item"
        />
      </li>
    </TransitionGroup>
  </nav>
</template>

<style scoped lang="scss">
.main-navigation > .list > .nav-item-transition-enter-from {
  transform: translateY(calc((var(--index) + 1) * 500%));
}

.main-navigation > .list > .nav-item-transition-enter-to {
  transform: translateY(0);
}

.main-navigation > .list > .nav-item-transition-enter-active {
  transition: transform 1s var(--base-transition-easing);
}

.main-navigation > .list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.main-navigation > .list > .item {
  display: flex;
  align-items: baseline;
  gap: 2rem;
}

.main-navigation > .list > .item > .link {
  position: relative;
  hyphens: auto;
  transition: color 0.5s var(--base-transition-easing, ease);

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -0.25rem;
    width: 100%;
    background-color: currentColor;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease-in-out;

    @media (--vs) {
      height: 3px;
    }

    @media (--vl) {
      height: 5px;
    }
  }

  &.-is-active::after {
    transform: scaleX(1);
  }

  @media (hover: hover) {
    &:hover::after {
      transform: scaleX(1);
    }
  }
}
</style>
